import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCheck, CircleDollarSign, AtSign, Flag, MessageSquare, Bell, CheckCircle2, PenLine } from 'lucide-react';
import AppLayout from '../components/AppLayout';
import PetanqueLoader from '../components/PetanqueLoader';
import { api } from '../api/sangly';
import { timeAgo } from '../lib/format';
import { cn } from '../lib/utils';

const KIND_ICON = { paid: CircleDollarSign, mention: AtSign, flagged: Flag, comment: MessageSquare, resolved: CheckCircle2, update: PenLine };

export default function Notifications() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems(await api.notifications.list()); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const open = async (n) => {
    if (!n.read) { try { await api.notifications.markRead(n.id); } catch { /* ignore */ } }
    if (n.proforma) navigate(`/proformas/${n.proforma}`);
    else if (n.refund) navigate('/refunds?tab=to_finalize');
    else if (n.supplier) navigate(`/suppliers/${n.supplier}`);
    else if (n.invoice) navigate(`/invoices/${n.invoice}`);
    else load();
  };
  const markAll = async () => { await api.notifications.markAllRead().catch(() => {}); load(); };

  return (
    <AppLayout title="Notifications" description="Payments confirmed, mentions & follow-up updates"
      actions={
        <button onClick={markAll}
          className="inline-flex h-9 items-center gap-1.5 rounded-md border border-border bg-card px-3 text-[13px] hover:bg-muted">
          <CheckCheck className="h-4 w-4" /> Mark all read
        </button>
      }>
      <div className="mx-auto max-w-3xl">
        {loading ? (
          <div className="grid h-48 place-items-center"><PetanqueLoader size={34} roll /></div>
        ) : items.length === 0 ? (
          <div className="grid h-48 place-items-center rounded-lg border border-dashed border-border text-center">
            <div>
              <Bell className="mx-auto mb-2 h-7 w-7 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">No notifications yet.</p>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((n) => {
              const Icon = KIND_ICON[n.kind] || Bell;
              return (
                <button key={n.id} onClick={() => open(n)}
                  className={cn('flex w-full items-start gap-3 rounded-lg border p-3.5 text-left shadow-card transition-colors hover:border-primary/40',
                    n.read ? 'border-border bg-card' : 'border-primary/30 bg-primary/[0.05]')}>
                  <span className={cn('mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-md',
                    ['paid', 'resolved'].includes(n.kind) ? 'bg-emerald-500/12 text-emerald-600' : 'bg-primary/12 text-primary')}>
                    <Icon className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-[14px] font-medium">{n.title}</span>
                      {!n.read && <span className="h-2 w-2 shrink-0 rounded-full bg-primary" />}
                      <span className="ml-auto shrink-0 text-[11px] text-muted-foreground">{timeAgo(n.created_at)}</span>
                    </div>
                    {n.body && <p className="mt-1 whitespace-pre-wrap text-[13px] text-muted-foreground">{n.body}</p>}
                    {(n.invoice_supplier || n.invoice_name) && (
                      <p className="mt-1 text-[12px] text-muted-foreground">
                        {n.invoice_supplier} · <span className="font-mono">{n.invoice_name}</span>
                      </p>
                    )}
                    {n.proforma_label && (
                      <p className="mt-1 text-[12px] text-muted-foreground">Pro-forma · {n.proforma_label}</p>
                    )}
                    {n.refund_label && (
                      <p className="mt-1 text-[12px] text-muted-foreground">Refund · {n.refund_label}</p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
